import { addressesNew } from "../models/addresses.service"
import { Zones } from "../models/offered-services.service"
import { httpGet, httpGetRoute, httpPost } from "./http.service"
import { I18nService } from "./i18n.service"
import { isValidJson } from "./utils.service"

export type TransportMode = 'car' | 'pedestrian' | 'bicycle' | 'scooter' | 'transit'

export interface GeocodeResult {
  address_components: { long_name: string, short_name: string, types: (keyof Zones[0])[] }[]
  formatted_address: string
  types: string[]
}

export interface GeojsonResult {
  type: 'Feature'
  properties: any
  bbox: [number, number, number, number]
  geometry: { type: 'Point' | 'Polygon', coordinates: [number, number][] }
}

export interface RoutingParams {
  origin: [number, number]
  destination: [number, number]
  arrivalTime: string
  transportMode: TransportMode
  recurring: boolean
}

export interface RoutingResult {
  sections: {
    arrival: { time: string }
    departure: { time: string }
    travelSummary: { duration: number, length: number }
    polyline: string
  }[]
}

const i18nScoped = new I18nService(require('./geocoding.service.yml'))

export function geocodingGeodesicDistance (from: { latitude: number, longitude: number } | { lat: number, lng: number }, to: { latitude: number, longitude: number }  | { lat: number, lng: number }, decimal = 2) {
  from = 'lat' in from ? { latitude: from.lat, longitude: from.lng } : from
  to = 'lat' in to ? { latitude: to.lat, longitude: to.lng } : to

  const radLat = (from.latitude - to.latitude) * (Math.PI / 180)
  const radLng = (from.longitude - to.longitude) * (Math.PI / 180)
  const a = Math.sin(radLat / 2) * Math.sin(radLat / 2) + Math.cos(from.latitude * (Math.PI / 180)) * Math.cos(to.latitude * (Math.PI / 180)) * Math.sin(radLng / 2) * Math.sin(radLng / 2)
  return Math.round((12742 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * (10 ^ decimal))) / (10 ^ decimal)
}

/** This method call our caching server. Approximation to nearest kilometer makes the cache hit rate higher. */
export function geocodingReverseApproximative (latitude: number, longitude: number): Promise<{ data: GeocodeResult }[]> {
  return httpGet(`/geocode/reverse_geocode_approximative?latitude=${latitude}&longitude=${longitude}`)
}

export async function geocodingZoneGeojson (zone: Zones[0]): Promise<GeojsonResult> {
  const zoneKeys = Object.keys(zone)
  let searchQuery = ''

  if (zoneKeys.length === 1 && zoneKeys[0] === 'country') {
    // When the displayed zone is a full country, only the ISO code is stored in the zone object.
    // Because of that, searches like 'Re' could be sent to OpenStreetMap, resulting in ambiguous results (ex. returning a region of Italy instead of the country Réunion).
    // To avoid that, we resolve the country name from the ISO code and use it as the search query.
    const countryCache = window.localStorage.getItem(`geocode/country-code-to-name/${zone.country}`)

    if (countryCache) {
      searchQuery = countryCache
    } else {
      const countries = (await addressesNew()).countries
      const country = countries.find(country => country.id === zone.country)?.text
      window.localStorage.setItem(`geocode/country-code-to-name/${zone.country}`, country)
      searchQuery = country
    }
  } else {
    const orderedKeys = ['route', 'locality', 'postal_code', 'zipcode', 'administrative_area_level_1', 'administrative_area_level_2', 'country']
    searchQuery = Object.keys(zone)
      .sort((a, b) => orderedKeys.indexOf(a) - orderedKeys.indexOf(b))
      .map(key => zone[key as keyof Zones[0]])
      .join(', ')
  }

  const cache = window.localStorage.getItem(`geojson/${searchQuery}`)
  if (cache && isValidJson(cache)) return JSON.parse(cache)

  const results = await httpGet('https://nominatim.openstreetmap.org/search', { q: searchQuery, format: 'geojson', polygon_geojson: 1, polygon_threshold: 0.001, limit: 1 })
  window.localStorage.setItem(`geojson/${searchQuery}`, JSON.stringify(results.features[0]))
  return results.features[0]
}

export async function geocodingRouting ({ origin, destination, arrivalTime, transportMode, ...body }: RoutingParams): Promise<RoutingResult> {
  return httpPost(`/${httpGetRoute()}/geocode/georouting`, { geocoding_routing_cache: { ...body, origin: origin.join(','), destination: destination.join(','), arrival_time: arrivalTime, transport_mode: transportMode } })
}

export function geocodingRoutingUrl (origin: [number, number], destination: [number, number]) {
  return `https://www.google.com/maps/dir/${origin.join(',')}/${destination.join(',')}`
}

export function geocodingi18nTransportMode (transportMode: TransportMode) {
  return i18nScoped.t('transport_modes.' + transportMode)
}
